"use client"
import { useState } from "react"
import type { Ad } from "@/app/generated/prisma/client"

const CATEGORY_LABELS: Record<string, string> = {
  "travel": "Travel",
  "art-supplies": "Art Supplies",
  "framing": "Framing",
  "housing": "Housing",
  "grants": "Grants",
  "other": "Sponsored",
}

export function ContextualAd({ ad }: { ad: Ad }) {
  const [clicked, setClicked] = useState(false)

  async function handleClick() {
    if (clicked) return
    setClicked(true)
    try {
      await fetch(`/api/ads/${ad.id}/click`, { method: "POST" })
    } catch {
      // best effort
    }
    window.open(ad.url, "_blank", "noopener,noreferrer")
  }

  return (
    <aside
      className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--background)] hover:border-[var(--primary)] transition-colors cursor-pointer group"
      onClick={handleClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      aria-label={`Sponsored: ${ad.title}`}
    >
      {ad.imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={ad.imageUrl}
          alt=""
          className="w-full h-32 object-cover"
        />
      )}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-widest text-[var(--muted-foreground)] font-semibold">
            {CATEGORY_LABELS[ad.category] ?? "Sponsored"}
          </span>
          <span className="text-[10px] text-[var(--muted-foreground)] border border-[var(--border)] px-1.5 py-0.5 rounded">
            Ad
          </span>
        </div>
        <p className="text-sm font-semibold leading-snug mb-1 group-hover:text-[var(--primary)] transition-colors">
          {ad.title}
        </p>
        {ad.body && (
          <p className="text-xs text-[var(--muted-foreground)] leading-relaxed mb-3">
            {ad.body}
          </p>
        )}
        <span className="inline-block text-xs font-medium text-[var(--primary)] underline underline-offset-2">
          {ad.cta ?? "Learn more"} →
        </span>
      </div>
    </aside>
  )
}

// Compact inline version for directory cards / feed
export function ContextualAdInline({ ad }: { ad: Ad }) {
  const [clicked, setClicked] = useState(false)

  async function handleClick() {
    if (clicked) return
    setClicked(true)
    try {
      await fetch(`/api/ads/${ad.id}/click`, { method: "POST" })
    } catch {
      // best effort
    }
    window.open(ad.url, "_blank", "noopener,noreferrer")
  }

  return (
    <div
      className="flex items-center gap-3 border border-[var(--border)] rounded-xl px-4 py-3 cursor-pointer hover:border-[var(--primary)] transition-colors group"
      onClick={handleClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      {ad.imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={ad.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold truncate group-hover:text-[var(--primary)] transition-colors">
          {ad.title}
        </p>
        {ad.body && <p className="text-xs text-[var(--muted-foreground)] truncate">{ad.body}</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <span className="text-[9px] border border-[var(--border)] px-1 rounded text-[var(--muted-foreground)]">Ad</span>
        <span className="text-xs text-[var(--primary)] font-medium">{ad.cta ?? "Learn more"} →</span>
      </div>
    </div>
  )
}
